import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface CarePlanWithClient {
  id: string;
  client_id: string;
  client_name: string;
  plan_type: string;
  services: string[];
  status: string;
  notes: string;
  schedule: any;
  created_at: string;
}

interface Profile {
  id: string;
  full_name: string | null;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

const statusOptions = ["active", "paused", "completed", "cancelled"];

const serviceOptions = [
  "Personal Care",
  "Medication Reminders",
  "Meal Preparation",
  "Light Housekeeping",
  "Companionship",
  "Errands & Shopping",
  "Respite Care",
  "24-Hour Live-In Care",
  "Dementia & Parkinson's Support",
  "Private Duty",
  "Recovery Transition Support",
  "Safety & Mobility Support",
  "Daily Living Support",
  "Overnight Care",
];

export default function AdminCarePlans() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [plans, setPlans] = useState<CarePlanWithClient[]>([]);
  const [clients, setClients] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedPlan, setSelectedPlan] = useState<CarePlanWithClient | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    fetchData();

    const channel = supabase
      .channel("care-plans-admin")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "care_plans" },
        (payload) => {
          fetchData();
          if (payload.eventType === 'INSERT') showToast('New care plan created', 'success');
          if (payload.eventType === 'UPDATE') showToast('Care plan updated', 'info');
          if (payload.eventType === 'DELETE') showToast('Care plan deleted', 'error');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchData = async () => {
    const { data: plansData } = await supabase
      .from("care_plans")
      .select("*, client:profiles!care_plans_client_id_fkey(full_name)")
      .order("created_at", { ascending: false });

    const mapped: CarePlanWithClient[] = (plansData || []).map((p: any) => ({
      id: p.id,
      client_id: p.client_id,
      client_name: p.client?.full_name || "Unknown",
      plan_type: p.plan_type,
      services: p.services || [],
      status: p.status,
      notes: p.notes || "",
      schedule: p.schedule,
      created_at: p.created_at,
    }));
    setPlans(mapped);

    const { data: clData } = await supabase
      .from("profiles")
      .select("id, full_name")
      .eq("role", "client");
    setClients(clData || []);

    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: "bg-brand-green-50 text-brand-green-700",
      paused: "bg-amber-50 text-amber-700",
      completed: "bg-blue-50 text-blue-700",
      cancelled: "bg-red-50 text-red-700",
    };
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${styles[status] || "bg-gray-100 text-gray-600"}`}
      >
        {status}
      </span>
    );
  };

  const filtered = plans.filter((p) => {
    const matchesStatus = filterStatus === "all" || p.status === filterStatus;
    const term = search.toLowerCase();
    const matchesSearch =
      p.client_name.toLowerCase().includes(term) ||
      p.plan_type.toLowerCase().includes(term) ||
      p.services.some((s) => s.toLowerCase().includes(term));
    return matchesStatus && matchesSearch;
  });

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const clientId = fd.get("client_id") as string;
    const planType = fd.get("plan_type") as string;
    const notes = fd.get("notes") as string;
    const selectedServices = fd.getAll("services") as string[];

    const { error } = await supabase.from("care_plans").insert({
      client_id: clientId,
      plan_type: planType,
      services: selectedServices,
      notes,
      status: "active",
    });

    if (error) {
      setFormMsg("Failed to create care plan.");
    } else {
      setFormMsg("Care plan created successfully!");
      setTimeout(() => {
        setShowNewModal(false);
        setFormMsg("");
        fetchData();
      }, 1200);
    }
    setSubmitting(false);
  };

  const handleEdit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedPlan) return;
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const planType = fd.get("plan_type") as string;
    const status = fd.get("status") as string;
    const notes = fd.get("notes") as string;
    const selectedServices = fd.getAll("services") as string[];

    const { error } = await supabase
      .from("care_plans")
      .update({
        plan_type: planType,
        services: selectedServices,
        status,
        notes,
      })
      .eq("id", selectedPlan.id);

    if (error) {
      setFormMsg("Failed to update care plan.");
    } else {
      setFormMsg("Care plan updated successfully!");
      setTimeout(() => {
        setShowEditModal(false);
        setSelectedPlan(null);
        setFormMsg("");
        fetchData();
      }, 1200);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;
    if (!confirm("Are you sure you want to delete this care plan? This cannot be undone.")) return;
    const { error } = await supabase.from("care_plans").delete().eq("id", selectedPlan.id);
    if (!error) {
      setSelectedPlan(null);
      fetchData();
    }
  };

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="Care Plans">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading care plans...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "active", "paused", "completed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    filterStatus === s
                      ? "bg-brand-green-600 text-white"
                      : "bg-white border border-gray-200 text-gray-600 hover:border-brand-green-300"
                  }`}
                >
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-56">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search plans..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line" />
                </div>
                New Plan
              </button>
            </div>
          </div>

          {/* Plans Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Plan Type
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Services
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((p) => (
                    <tr key={p.id} className="hover:bg-soft-gray transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">
                        {p.client_name}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-700">{p.plan_type}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {p.services.slice(0, 3).map((s) => (
                            <span
                              key={s}
                              className="px-2 py-0.5 rounded text-[10px] font-medium bg-brand-green-50 text-brand-green-700"
                            >
                              {s}
                            </span>
                          ))}
                          {p.services.length > 3 && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                              +{p.services.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">{statusBadge(p.status)}</td>
                      <td className="px-5 py-3 text-sm text-gray-500">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => setSelectedPlan(p)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line text-gray-500" />
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((p) => (
                <div key={p.id} className="p-4" onClick={() => setSelectedPlan(p)}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{p.client_name}</p>
                      <p className="text-xs text-gray-500">{p.plan_type}</p>
                    </div>
                    {statusBadge(p.status)}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.services.slice(0, 2).map((s) => (
                      <span
                        key={s}
                        className="px-2 py-0.5 rounded text-[10px] font-medium bg-brand-green-50 text-brand-green-700"
                      >
                        {s}
                      </span>
                    ))}
                    {p.services.length > 2 && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                        +{p.services.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-4">No care plans found.</p>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
                >
                  Create Care Plan
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View / Detail / Delete Modal */}
      {selectedPlan && !showEditModal && !showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedPlan(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Care Plan Details</h3>
              <button onClick={() => setSelectedPlan(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-500" />
                </div>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-green-100 flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-brand-green-600 text-lg" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selectedPlan.client_name}</p>
                  <p className="text-xs text-gray-500">{selectedPlan.plan_type}</p>
                </div>
              </div>
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span>{statusBadge(selectedPlan.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Created</span>
                  <span className="text-sm text-gray-900">{formatDate(selectedPlan.created_at)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block mb-2">Services</span>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedPlan.services.map((s) => (
                      <span key={s} className="px-2.5 py-1 rounded-full text-xs font-medium bg-brand-green-50 text-brand-green-700">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                {selectedPlan.notes && (
                  <div>
                    <span className="text-sm text-gray-500 block mb-1">Notes</span>
                    <p className="text-sm text-gray-600 bg-soft-gray rounded-lg p-3">{selectedPlan.notes}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-3">
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-all whitespace-nowrap"
                >
                  Delete Plan
                </button>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
                >
                  Edit Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Care Plan</h3>
            {formMsg && (
              <div
                className={`rounded-lg p-3 border mb-4 ${
                  formMsg.includes("success")
                    ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800"
                    : "bg-red-50 border-red-100 text-red-700"
                }`}
              >
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Client</label>
                <select
                  name="client_id"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none bg-white"
                >
                  <option value="">Select client...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name || "Unnamed"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Plan Type</label>
                <input
                  name="plan_type"
                  type="text"
                  required
                  placeholder="e.g., Standard Home Care, 24-Hour Care"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Services</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                  {serviceOptions.map((svc) => (
                    <label key={svc} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="services"
                        value={svc}
                        className="w-4 h-4 rounded border-gray-300 text-brand-green-600 focus:ring-brand-green-500"
                      />
                      <span className="text-xs text-gray-700">{svc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                  placeholder="Any special instructions or care notes..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Creating..." : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Care Plan</h3>
            {formMsg && (
              <div
                className={`rounded-lg p-3 border mb-4 ${
                  formMsg.includes("success")
                    ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800"
                    : "bg-red-50 border-red-100 text-red-700"
                }`}
              >
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Client</label>
                <p className="text-sm text-gray-900 bg-soft-gray rounded-lg px-3 py-2.5">
                  {selectedPlan.client_name}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Plan Type</label>
                <input
                  name="plan_type"
                  type="text"
                  required
                  defaultValue={selectedPlan.plan_type}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((s) => (
                    <label key={s} className="cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value={s}
                        defaultChecked={selectedPlan.status === s}
                        className="sr-only peer"
                      />
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap block border ${
                          selectedPlan.status === s
                            ? "bg-brand-green-600 text-white border-brand-green-600"
                            : "bg-white text-gray-600 border-gray-200 peer-checked:bg-brand-green-600 peer-checked:text-white peer-checked:border-brand-green-600 hover:border-brand-green-300"
                        }`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Services</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                  {serviceOptions.map((svc) => (
                    <label key={svc} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="services"
                        value={svc}
                        defaultChecked={selectedPlan.services.includes(svc)}
                        className="w-4 h-4 rounded border-gray-300 text-brand-green-600 focus:ring-brand-green-500"
                      />
                      <span className="text-xs text-gray-700">{svc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  defaultValue={selectedPlan.notes}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}